import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Clipboard } from '@angular/cdk/clipboard';
import { AnalyticsService } from '@gs-2.0/core';

/**
 * Interface for share dialog data
 */
export interface ShareDialogData {
  /**
   * The URL to share
   */
  url: string;

  /**
   * The title to share (for social media)
   */
  title?: string;

  /**
   * The description to share (for social media)
   */
  description?: string;
}

/**
 * Share Dialog Component
 * Displays a dialog with social media sharing options and a copyable link
 *
 * Features:
 * - Share to Facebook, LinkedIn, and X (Twitter)
 * - Copy link to clipboard
 * - Responsive design
 *
 * @example
 * ```typescript
 * this.dialog.open(ShareDialogComponent, {
 *   data: {
 *     url: 'https://example.com',
 *     title: 'Check this out',
 *     description: 'An amazing charity'
 *   }
 * });
 * ```
 */
@Component({
  selector: 'app-share-dialog',
  standalone: false,
  templateUrl: './share-dialog.component.html',
  styleUrl: './share-dialog.component.scss',
})
export class ShareDialogComponent {
  /**
   * Flag to show copy success feedback
   */
  copySuccess = false;

  constructor(
    public dialogRef: MatDialogRef<ShareDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ShareDialogData,
    private clipboard: Clipboard,
    private analyticsService: AnalyticsService,
  ) {}

  /**
   * Closes the dialog
   */
  onClose(): void {
    this.dialogRef.close();
  }

  /**
   * Shares to Facebook
   */
  shareToFacebook(): void {
    this.analyticsService.trackShareClick('Facebook');
    const url = encodeURIComponent(this.data.url);
    window.open(
      `https://www.facebook.com/sharer/sharer.php?u=${url}`,
      '_blank'
    );
  }

  /**
   * Shares to LinkedIn
   */
  shareToLinkedIn(): void {
    this.analyticsService.trackShareClick('LinkedIn');
    const url = encodeURIComponent(this.data.url);
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      '_blank'
    );
  }

  /**
   * Shares to X (Twitter)
   */
  shareToX(): void {
    this.analyticsService.trackShareClick('X');
    const url = encodeURIComponent(this.data.url);
    const text = encodeURIComponent(this.data.title || '');
    window.open(
      `https://twitter.com/intent/tweet?url=${url}&text=${text}`,
      '_blank'
    );
  }

  /**
   * Copies the link to clipboard
   */
  copyLink(): void {
    this.analyticsService.trackShareClick('Copy');
    const success = this.clipboard.copy(this.data.url);
    if (success) {
      this.copySuccess = true;
      setTimeout(() => {
        this.copySuccess = false;
      }, 2000);
    }
  }
}
